"use client"

import { useEffect, useState } from "react"
import type { UpgradeDefinition, UpgradeNodeStatus } from "@/types/upgrade.types"
import { CloseIcon } from "@/components/icons/pixel-icons"
import { getUpgradeCost } from "@/systems/upgrade-system"

interface UpgradeBottomSheetProps {
  upgrade: UpgradeDefinition | null
  status: UpgradeNodeStatus
  canAfford: boolean
  purchaseCount: number
  onUnlock: () => void
  onClose: () => void
}

export const UpgradeBottomSheet = ({
  upgrade,
  status,
  canAfford,
  purchaseCount,
  onUnlock,
  onClose,
}: UpgradeBottomSheetProps) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (upgrade) {
      // Trigger animation after mount
      requestAnimationFrame(() => {
        setIsVisible(true)
      })
    } else {
      setIsVisible(false)
    }
  }, [upgrade])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 200) // Wait for animation
  }

  if (!upgrade) return null

  const isMaxed = status === "maxed"
  const isUnlocked = status === "unlocked" || isMaxed
  const isBlocked = status === "blocked"
  const isAvailable = status === "available"
  const canPurchase = (isAvailable || status === "unlocked") && !isMaxed
  const nextCost = getUpgradeCost(upgrade, purchaseCount)

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-[70] transition-opacity duration-200 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Sheet */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[71] bg-gradient-to-t from-slate-900 to-slate-800 border-t border-slate-600/50 rounded-t-2xl shadow-2xl transition-transform duration-200 ${
          isVisible ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {/* Handle */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-12 h-1 bg-slate-600 rounded-full" />
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1 text-slate-400 hover:text-white transition"
        >
          <CloseIcon size={20} />
        </button>

        {/* Content */}
        <div className="px-6 pb-8 pt-2">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            {/* Icon */}
            <div
              className={`w-16 h-16 rounded-xl border-2 flex items-center justify-center flex-shrink-0 overflow-hidden ${
                isUnlocked
                  ? "bg-emerald-900/60 border-emerald-500/70"
                  : isBlocked
                    ? "bg-red-950/40 border-red-800/50"
                    : "bg-amber-900/50 border-amber-500/60"
              }`}
            >
              {isBlocked ? (
                <span className="text-2xl text-red-400">✕</span>
              ) : upgrade.icon ? (
                <img
                  src={upgrade.icon}
                  alt={upgrade.name}
                  className="w-full h-full object-cover"
                  style={{ imageRendering: "pixelated" }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-amber-600/60 to-amber-800/60" />
              )}
            </div>

            {/* Title & Type */}
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-white mb-1">
                {upgrade.name}
                {purchaseCount > 0 && (
                  <span className="text-slate-400 font-normal text-base ml-2">
                    ({purchaseCount}/{upgrade.maxPurchases})
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                {upgrade.type === "active" && (
                  <span className="px-2 py-0.5 bg-purple-600/50 border border-purple-500/50 rounded text-[10px] text-purple-200 font-bold">
                    ACTIVE
                  </span>
                )}
                {upgrade.type === "passive" && (
                  <span className="px-2 py-0.5 bg-blue-600/50 border border-blue-500/50 rounded text-[10px] text-blue-200 font-bold">
                    PASSIVE
                  </span>
                )}
                {isMaxed && (
                  <span className="px-2 py-0.5 bg-amber-600/50 border border-amber-500/50 rounded text-[10px] text-amber-200 font-bold">
                    MAXED
                  </span>
                )}
                {purchaseCount > 0 && !isMaxed && (
                  <span className="px-2 py-0.5 bg-emerald-600/50 border border-emerald-500/50 rounded text-[10px] text-emerald-200 font-bold">
                    LVL {purchaseCount}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-slate-300 text-sm mb-6 leading-relaxed">
            {upgrade.description}
          </p>

          {/* Status message for blocked */}
          {isBlocked && (
            <div className="bg-red-950/50 border border-red-800/50 rounded-lg p-3 mb-4">
              <p className="text-red-300 text-sm">
                This upgrade is blocked. You chose the other branch in this column.
              </p>
            </div>
          )}

          {/* Purchase button - show when can purchase more */}
          {canPurchase && !isBlocked && (
            <button
              onClick={onUnlock}
              disabled={!canAfford}
              className={`w-full py-3 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                canAfford
                  ? "bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white shadow-lg shadow-amber-500/30"
                  : "bg-slate-700/50 text-slate-500 cursor-not-allowed"
              }`}
            >
              <img
                src="/images/icon-coins.png"
                alt=""
                className="w-5 h-5"
                style={{ imageRendering: "pixelated" }}
              />
              <span>
                {canAfford
                  ? purchaseCount > 0
                    ? `Upgrade for ${nextCost}`
                    : `Unlock for ${nextCost}`
                  : `Need ${nextCost} gold`}
              </span>
            </button>
          )}

          {/* Locked state - prerequisites not met */}
          {status === "locked" && !isBlocked && (
            <div className="w-full py-3 rounded-xl bg-slate-700/50 border border-slate-600/50 text-center">
              <span className="text-slate-400 font-bold">Prerequisites Not Met</span>
            </div>
          )}

          {/* Maxed out */}
          {isMaxed && (
            <div className="w-full py-3 rounded-xl bg-amber-900/50 border border-amber-600/50 text-center">
              <span className="text-amber-300 font-bold">Fully Upgraded!</span>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
